import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBetSlip } from "@/context/BetSlipContext";
import { toast } from "sonner";
import { Icon } from "@/components/Icon";
import { SBOddsButton, LivePill, ClockChip, initials } from "@/components/sportsbook/sb";
import TeamLogo from "@/components/sportsbook/TeamLogo";
import { leagueLabel } from "@/components/sportsbook/SBBoardGame";
import SportsNavigation from "@/components/sportsbook/SportsNavigation";
import LeagueSectionPremium from "@/components/sportsbook/LeagueSection";
import GameMarketRow from "@/components/sportsbook/GameMarketRow";
import FeaturedEventHero from "@/components/sportsbook/FeaturedEventHero";
import DiscoveryGameCard from "@/components/sportsbook/DiscoveryGameCard";
import { BoardSkeleton, BoardError } from "@/components/sportsbook/Skeletons";
import EmptyState from "@/components/ui/EmptyState";
import { useOddsFormat, setOddsFormat } from "@/lib/odds";
import { sortSportsbookEvents, sortLeagueBuckets, getEventPriority, hasStartedPregame } from "@/lib/sportsbookPriority";

const SPREAD_TITLE_UC = { baseball: "RUN LINE", hockey: "PUCK LINE", soccer: "HANDICAP" };

// case-insensitive sport key (the board data uses "Football" / "Esports" etc.)
const sk = (s) => (s || "").toLowerCase();

const SPORT_LABEL = {
  soccer: "Soccer", basketball: "Basketball", football: "Football",
  baseball: "Baseball", tennis: "Tennis", mma: "MMA", hockey: "Hockey",
  esports: "Esports", golf: "Golf", boxing: "Boxing", other: "Sports",
};

const SPORT_ICON = {
  soccer: "soccer", basketball: "basketball", football: "football",
  baseball: "baseball", tennis: "tennis", mma: "mma", hockey: "hockey",
  esports: "esports", golf: "golf", boxing: "boxing", other: "all-sports",
};

// Mockup nav taxonomy (SBX-01, §2.5): a curated set of MAJOR leagues get their own
// tile (NFL, NCAAF, NBA, WNBA, NCAAB, MLB, NHL, UFC); every other league groups under
// its SPORT tile (Basketball, Soccer, Tennis, Golf, Boxing, Esports). Real data carries
// hundreds of minor leagues, so only the majors are promoted. A "tile token" is the
// major-league display name, otherwise the sport key.
const MAJOR_LEAGUE_TILE = {
  "nfl": "NFL",
  "ncaaf": "NCAAF", "college football": "NCAAF", "cfb": "NCAAF",
  "nba": "NBA",
  "wnba": "WNBA",
  "ncaab": "NCAAB", "college basketball": "NCAAB", "cbb": "NCAAB",
  "mlb": "MLB",
  "nhl": "NHL",
  "ufc": "UFC",
};
const normLeague = (l) => String(l || "").trim().toLowerCase();
// Promote to a major-league tile by exact league match OR a whole-word token match, so variants like
// "NBA Preseason" / "NHL Preseason" still land on their own tile instead of the generic sport bucket.
// Order is specific-first (ncaaf/ncaab/wnba before nfl/nba) so no token is swallowed by a shorter one.
const MAJOR_TOKEN_TILES = [["ncaaf", "NCAAF"], ["ncaab", "NCAAB"], ["wnba", "WNBA"], ["nfl", "NFL"], ["nba", "NBA"], ["mlb", "MLB"], ["nhl", "NHL"], ["ufc", "UFC"]];
const majorTileFor = (e) => {
  const nl = normLeague(e.league);
  if (MAJOR_LEAGUE_TILE[nl]) return MAJOR_LEAGUE_TILE[nl];
  for (const [tok, label] of MAJOR_TOKEN_TILES) if (new RegExp(`\\b${tok}\\b`).test(nl)) return label;
  return null;
};
const tileTokenFor = (e) => majorTileFor(e) || sk(e.sport);
const tileLabelFor = (e) => majorTileFor(e) || SPORT_LABEL[sk(e.sport)] || e.sport;

// Group events into league buckets, then order buckets by the CANONICAL sportsbook priority
// (premium leagues first) and order each bucket's events by the same priority/chronology rules
// — so the board never depends on raw /events encounter order (P0-A).
function groupByLeague(list) {
  const buckets = [];
  const idx = {};
  list.forEach((e) => {
    const key = `${e.sport}|${e.league || "Other"}`;
    if (idx[key] == null) {
      idx[key] = buckets.length;
      buckets.push({ key, sport: e.sport, league: e.league || SPORT_LABEL[sk(e.sport)] || "Sports", events: [] });
    }
    buckets[idx[key]].events.push(e);
  });
  buckets.forEach((b) => { b.events = sortSportsbookEvents(b.events); });
  return sortLeagueBuckets(buckets);
}

// Sport-aware section title for the spread market (matches the mockup: RUN LINE / PUCK LINE).
function spreadTitle(sport) {
  if (sport === "baseball") return "Run line";
  if (sport === "hockey") return "Puck line";
  if (sport === "soccer") return "Handicap";
  return "Spread";
}
const MARKET_TITLE = { moneyline: "Moneyline", spread: "Spread", total: "Total" };
const MARKET_ORDER = ["moneyline", "spread", "total"];

// Group an event's outcomes by market for rendering. Legacy (untagged) -> single group.
function groupOutcomes(outcomes) {
  if (!outcomes.some((o) => o.market)) return [{ market: null, items: outcomes }];
  const by = {};
  outcomes.forEach((o) => { (by[o.market] = by[o.market] || []).push(o); });
  return MARKET_ORDER.filter((m) => by[m]).map((m) => ({ market: m, items: by[m] }));
}

// Compact odds-button label: abbreviate a leading team name to its monogram so the
// button reads "KC", "KC −2.5" etc. (the full team names sit in the card header).
function shortLabel(label, event) {
  if (!label) return label;
  for (const t of [event.home_team, event.away_team]) {
    if (!t) continue;
    if (label === t) return initials(t);
    if (label.startsWith(t)) return `${initials(t)}${label.slice(t.length)}`;
  }
  return label;
}

// A standard two-team game (renders as the DK-style columnar row); everything
// else (outrights, prop parents) keeps the stacked EventCard.
const isGame = (e) =>
  !["outright", "prop"].includes(e.event_type) && e.home_team && e.away_team;

export default function Sportsbook() {
  const { user } = useAuth();
  const { toggle, has, count } = useBetSlip();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [promos, setPromos] = useState([]);
  const [sportFilter, setSportFilter] = useState("all");
  const [searchParams, setSearchParams] = useSearchParams();
  // Bug #1: ?view=live must initialise + stay in sync with the live view, both ways.
  const view = searchParams.get("view") === "live" ? "live" : "all";
  const setView = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v === "live") next.set("view", "live"); else next.delete("view");
    setSearchParams(next, { replace: true });
  };
  const [collapsed, setCollapsed] = useState({}); // { [leagueKey]: true }
  const toggleLeague = (key) => setCollapsed((c) => ({ ...c, [key]: !c[key] }));
  const oddsFmt = useOddsFormat();
  // Price-move arrows (▲/▼): remember the previous decimal price per outcome id.
  const prevOdds = useRef({});
  const moveFor = (outcomeId) => prevOdds.current[outcomeId] || null;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/events");
      setEvents(data);
      setError(false);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const silentLoad = async () => {
    try {
      const { data } = await api.get("/events");
      setEvents(data);
      setError(false);
    } catch (e) { /* keep showing the last good board */ }
  };

  useEffect(() => {
    load();
    api.get("/promotions").then(({ data }) => setPromos(data)).catch(() => {});
    const t = setInterval(silentLoad, 1000);
    return () => clearInterval(t);
  }, []);

  // Detect price moves vs the last snapshot → feed the ▲/▼ arrows (cleared after ~3s).
  const lastOdds = useRef({});
  useEffect(() => {
    const now = {};
    events.forEach((e) => (e.outcomes || []).forEach((o) => {
      now[o.id] = o.odds;
      const prev = lastOdds.current[o.id];
      if (prev != null && prev !== o.odds) {
        prevOdds.current[o.id] = o.odds > prev ? "up" : "down";
        setTimeout(() => { delete prevOdds.current[o.id]; }, 3000);
      }
    }));
    lastOdds.current = { ...lastOdds.current, ...now };
  }, [events]);

  const pick = (event, outcome) => {
    const eventLabel = event.event_type === "outright" && event.title ? event.title : `${event.away_team} @ ${event.home_team}`;
    // enrich the slip leg with league / live / sport so the slip can show the market·league chip
    toggle({
      eventId: event.id, outcomeId: String(outcome.id), label: `${outcome.label}`, odds: outcome.odds,
      marketVersion: outcome.market_version ?? null, line: outcome.line ?? null, side: outcome.side ?? null,
      receivedAt: outcome.received_at ?? null, providerUpdatedAt: outcome.provider_updated_at ?? null,
      expiresAt: outcome.expires_at ?? null,
      eventLabel, market: outcome.market, league: event.league || null, sport: event.sport || null,
      live: event.status === "live",
    });
  };

  const propsByParent = {};
  events.forEach((e) => {
    if (e.event_type === "prop" && e.parent_event_id) {
      (propsByParent[e.parent_event_id] = propsByParent[e.parent_event_id] || []).push(e);
    }
  });
  const isChildProp = (e) => e.event_type === "prop" && e.parent_event_id;
  const bySport = (sportFilter === "all" ? events : events.filter((e) => tileTokenFor(e) === sportFilter)).filter((e) => !isChildProp(e));
  const liveEvents = bySport.filter((e) => e.status === "live");
  const filteredEvents = view === "live" ? liveEvents : bySport;
  const openEvents = filteredEvents.filter((e) => e.status === "open" || e.status === "paused");
  const settledEvents = filteredEvents.filter((e) => e.status === "settled" || e.status === "closed");
  const isPicked = (eventId, outcomeId) => has(eventId, outcomeId);
  const liveCount = bySport.filter((e) => e.status === "live").length;

  // Nav tiles — one per league (big US sports) or per sport (others), ordered by the
  // mockup taxonomy: sport order, then league preference within a split sport.
  const boardEvents = events.filter((e) => !isChildProp(e));
  const tileMap = {};
  boardEvents.forEach((e) => {
    const token = tileTokenFor(e);
    if (!tileMap[token]) tileMap[token] = { id: token, label: tileLabelFor(e), icon: SPORT_ICON[sk(e.sport)] || "all-sports", count: 0, prio: Infinity };
    tileMap[token].count++;
    tileMap[token].prio = Math.min(tileMap[token].prio, getEventPriority(e)); // rank a tile by its BEST competition
  });
  // Nav tiles ordered by canonical priority (premium leagues/sports first), ties by label.
  const availableTiles = Object.values(tileMap).sort((a, b) =>
    (a.prio !== b.prio) ? a.prio - b.prio : String(a.label).localeCompare(String(b.label))
  );
  const activeTileLabel = tileMap[sportFilter]?.label || sportFilter;

  // Featured event for the "featured market" card — the highest-PRIORITY real live/open game
  // with markets (premium inventory preferred), no fabricated numbers.
  const sortedBoard = sortSportsbookEvents(boardEvents);
  const featuredEvent =
    sortedBoard.find((e) => e.status === "live" && isGame(e) && (e.outcomes || []).length) ||
    sortedBoard.find((e) => (e.status === "open" || e.status === "paused") && isGame(e) && (e.outcomes || []).length) ||
    null;

  const betGet = promos.find((p) => p.promo_type === "bet_and_get" && !user?.betget_claimed) || null;
  const otherPromos = promos.filter((p) => p.promo_type !== "bet_and_get");
  const freeBetCount = (user?.free_bets || []).length;

  // Render one league bucket with the premium LeagueSection + GameMarketRow (games)
  // or the stacked EventCard (outrights/props). `mobile` toggles the compact grid.
  const renderBucket = (bucket, mobile) => {
    const sportKey = sk(bucket.sport);
    const liveN = bucket.events.filter((e) => e.status === "live").length;
    return (
      <LeagueSectionPremium testid={bucket.key}
        sportIcon={SPORT_ICON[sportKey] || "all-sports"} league={leagueLabel(bucket.league)}
        games={bucket.events.length} live={liveN} spreadTitle={SPREAD_TITLE_UC[sportKey] || "SPREAD"}
        collapsed={!!collapsed[bucket.key]} onToggle={() => toggleLeague(bucket.key)} mobile={mobile}>
        {bucket.events.map((event) =>
          isGame(event)
            ? <GameMarketRow key={event.id} event={event} onPick={pick} isPicked={isPicked} moveFor={moveFor} mobile={mobile} sgp={false} navigable />
            : <div key={event.id} className="p-4"><EventCard event={event} index={0} isPicked={isPicked} onPick={pick} childProps={propsByParent[event.id] || []} shortLabel={shortLabel} /></div>
        )}
      </LeagueSectionPremium>
    );
  };
  const renderBuckets = (list) => groupByLeague(list).map((bucket) => (
    <React.Fragment key={bucket.key}>
      <div className="hidden lg:block">{renderBucket(bucket, false)}</div>
      <div className="lg:hidden">{renderBucket(bucket, true)}</div>
    </React.Fragment>
  ));

  return (
    <div className="min-h-screen overflow-x-clip bg-surface-1 text-white">
      <div className={`px-4 py-8 sm:px-6 ${count > 0 ? "lg:pr-[360px]" : ""}`}>

        {/* Page header — LIVE MARKETS kicker + title, with the market toggle on the right */}
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="mb-1 text-[11px] font-extrabold uppercase tracking-[0.24em] text-[#00D632]">PRYMEBET Sportsbook</p>
            <h1 className="font-display text-[26px] font-extrabold leading-none sm:text-[32px] lg:text-[40px]" style={{ letterSpacing: "-.04em" }}>Sportsbook</h1>
          </div>
          <button type="button" data-testid="sb-search-open" onClick={() => window.dispatchEvent(new CustomEvent("pnr-open-sb-search"))}
            aria-label="Search teams, players and games"
            className="flex h-[44px] w-full max-w-[300px] items-center gap-2.5 rounded-xl bg-[#0E0E11] px-3.5 text-left text-[15px] text-[#71717a] transition-colors hover:text-[#a1a1aa] focus-ring"
            style={{ boxShadow: "inset 0 0 0 1px #26262B" }}>
            <Icon name="search" size={17} className="text-pnr-green" />
            <span className="flex-1 truncate">Teams, players, games</span>
            <span className="rounded border border-[#26262B] px-1.5 py-0.5 text-[10px] font-bold text-[#52525b]">Ctrl K</span>
          </button>
        </div>

        {/* Sport tiles — All · Live · per-sport (SportsNavigation) */}
        <div className="mb-6">
          <SportsNavigation
            items={availableTiles}
            active={sportFilter} view={view} liveCount={liveCount} totalCount={boardEvents.length}
            onAll={() => { setSportFilter("all"); setView("all"); }}
            onLive={() => setView("live")}
            onPick={(id) => { setSportFilter(id); setView("all"); }}
          />
        </div>

        {/* Featured marquee + promo (real featured event, no fabricated numbers) */}
        {featuredEvent && isGame(featuredEvent) && (
          <div className="mb-7">
            {/* Desktop: full marquee + promo. Mobile (<lg): compact discovery card (SBX-M1). */}
            <div className="hidden lg:block">
              <FeaturedEventHero event={featuredEvent} onPick={pick} isPicked={isPicked} freeBets={freeBetCount} promo={betGet} />
            </div>
            <div className="lg:hidden">
              <DiscoveryGameCard event={featuredEvent} onPick={pick} isPicked={isPicked} mobile />
            </div>
          </div>
        )}

        {/* Optional strip for other admin promos (deposit match etc.) — hidden when none */}
        {otherPromos.length > 0 && (
          <div className="mb-7 flex flex-wrap gap-3" data-testid="promo-banner">
            {otherPromos.map((p) => (
              <div key={p.id} data-testid={`promo-banner-${p.id}`}
                className="surface-premium flex items-center gap-3 rounded-xl px-4 py-3">
                <Icon name="promotions" size={18} className="shrink-0 text-[#00D632]" />
                <div>
                  <p className="font-display text-sm font-black tracking-tight text-[#00D632]">{p.title}</p>
                  <p className="text-xs text-[#a1a1aa]">{p.description || `${p.match_pct}% deposit match${p.max_bonus ? ` up to $${p.max_bonus}` : ""}`}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* View toggle (All games / Live now) + odds-format indicator — SBX-01 */}
        <div className="mb-4 hidden items-center gap-2 lg:flex" data-testid="view-toggle">
          <button type="button" onClick={() => { setSportFilter("all"); setView("all"); }} data-testid="board-view-all"
            className="flex h-9 items-center rounded-[9px] px-3 transition-colors focus-ring"
            style={view === "all"
              ? { background: "#1F1F23", boxShadow: "inset 0 1px 0 rgba(255,255,255,.07),0 1px 3px rgba(0,0,0,.45)", color: "#fff", font: "700 14px Outfit" }
              : { background: "transparent", color: "#a1a1aa", font: "600 14px Outfit" }}>
            All games
          </button>
          <button type="button" onClick={() => setView("live")} data-testid="board-view-live"
            className="flex h-9 items-center gap-2 rounded-[9px] px-3 transition-colors focus-ring"
            style={view === "live"
              ? { background: "#1F1F23", boxShadow: "inset 0 1px 0 rgba(255,255,255,.07),0 1px 3px rgba(0,0,0,.45)", color: "#fff", font: "700 14px Outfit" }
              : { background: "transparent", color: "#a1a1aa", font: "600 14px Outfit" }}>
            <span className="h-1.5 w-1.5 rounded-full bg-[#EF4444]" style={{ animation: "pnrPulse 1.6s ease-out infinite" }} />
            Live now
            <span className="font-display text-xs font-extrabold tabular-nums text-[#EF4444]">{liveCount}</span>
          </button>
          <button type="button" onClick={() => setOddsFormat(oddsFmt === "american" ? "decimal" : "american")}
            data-testid="odds-format-toggle" aria-label={`Odds format: ${oddsFmt === "decimal" ? "Decimal" : "American"}. Tap to switch.`}
            className="ml-auto rounded-md px-1 focus-ring" style={{ font: "500 12px Outfit", color: "#71717a" }}>
            Odds · <span className="font-bold text-[#D4D4D8]">{oddsFmt === "decimal" ? "Decimal" : "American"}</span>
          </button>
        </div>

        {loading && events.length === 0 ? (
          <BoardSkeleton />
        ) : error && events.length === 0 ? (
          <BoardError onRetry={load} />
        ) : (view === "live" ? liveEvents.length === 0 : (openEvents.length === 0 && settledEvents.length === 0 && liveEvents.length === 0)) ? (
          view === "live" ? (
            <div className="rounded-[18px] border border-dashed border-surface-border bg-[#101013] py-16 text-center" data-testid="empty-events">
              <p className="font-display text-[17px] font-extrabold text-white">No games are live right now</p>
              <p className="mx-auto mt-1.5 max-w-[320px] text-[13px] text-[#71717a]">Live games appear here as soon as they start.</p>
              <button onClick={() => setView("all")} className="mx-auto mt-4 inline-flex h-11 items-center rounded-xl px-4 text-[13px] font-bold text-white transition-colors hover:border-border-strong focus-ring" style={{ boxShadow: "inset 0 0 0 1px #26262B" }}>See upcoming games</button>
            </div>
          ) : (
            <EmptyState
              icon={sportFilter === "all" ? "all-sports" : "search"}
              testid="empty-events"
              message={sportFilter === "all" ? "No events yet. Check back soon — markets are being set up." : `No ${activeTileLabel} events right now.`}
              action={sportFilter === "all" ? null : { label: "View all sports", icon: "all-sports", onClick: () => { setSportFilter("all"); setView("all"); } }}
            />
          )
        ) : (
          <div className="space-y-7">
            {/* LIVE NOW — grouped by league */}
            {view === "all" && liveEvents.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between" data-testid="live-section-heading">
                  <p className="flex items-center gap-2 font-display text-xl font-black tracking-[-.02em] sm:text-2xl">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-[#EF4444]" /> Live now
                    <span className="rounded-full bg-[#EF4444]/15 px-2 py-0.5 text-xs font-bold text-[#EF4444]">{liveEvents.length}</span>
                  </p>
                </div>
                {renderBuckets(liveEvents)}
              </div>
            )}

            {/* UPCOMING — grouped by league */}
            {(view === "live" || openEvents.length > 0) && (
              <div className="space-y-4">
                {view === "all" && liveEvents.length > 0 && (
                  <p className="font-display text-xl font-black tracking-[-.02em] text-white sm:text-2xl">Upcoming</p>
                )}
                {renderBuckets(view === "live" ? liveEvents : openEvents)}
              </div>
            )}

            {/* SETTLED */}
            {view === "all" && settledEvents.length > 0 && (
              <div className="space-y-3">
                <p className="font-display text-xl font-black tracking-[-.02em] text-[#a1a1aa] sm:text-2xl">Settled &amp; closed</p>
                {settledEvents.map((event, i) => (
                  <EventCard key={event.id} event={event} index={i} isPicked={isPicked} onPick={pick} settled shortLabel={shortLabel} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* One premium event card (SBEventCard): status/clock row, two team rows with badges
   + live scores, MONEYLINE / SPREAD / TOTAL as SBOddsButtons, an optional inline
   props expander, and the "All markets & player props" footer link. */
function EventCard({ event, index, isPicked, onPick, settled, childProps = [], shortLabel }) {
  const locked = !(event.status === "open" || event.status === "live") || hasStartedPregame(event);
  const paused = event.status === "paused";
  const isLive = event.status === "live";
  const [showProps, setShowProps] = useState(false);
  const activeProps = (childProps || []).filter((p) => p.status === "open" || p.status === "paused");
  const groups = groupOutcomes((event.outcomes || []).filter((o) => o.market !== "prop"));
  const isOutright = ["outright", "prop"].includes(event.event_type);
  const trueOutright = event.event_type === "outright";
  const activeOutrightOutcomes = trueOutright
    ? (event.outcomes || []).filter((o) => o.active !== false && !o.hidden)
    : (event.outcomes || []);
  const largeOutright = trueOutright && activeOutrightOutcomes.length > 12;
  const boardOutcomes = largeOutright
    ? [...activeOutrightOutcomes].sort((a, b) => Number(a.odds || 99999) - Number(b.odds || 99999) || String(a.label).localeCompare(String(b.label))).slice(0, 6)
    : activeOutrightOutcomes;

  // live vs final scores
  const liveScore = isLive && event.gs_home_score != null;
  const awayScore = liveScore ? event.gs_away_score : (event.away_score != null ? event.away_score : null);
  const homeScore = liveScore ? event.gs_home_score : (event.home_score != null ? event.home_score : null);
  const marketCount = event.market_count || ((event.outcomes || []).length + activeProps.reduce((a, p) => a + (p.outcomes?.length || 0), 0));

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.03, 0.24) }}
      data-testid={`event-${event.id}`}
      className={`surface-premium rounded-2xl p-4 sm:p-5 ${isLive ? "!border-[#EF4444]/35" : ""}`}
    >
      {/* status row */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {isLive ? (
            <>
              <LivePill data-testid={`live-badge-${event.id}`} />
              {event.gs_period && <ClockChip>{event.gs_period}</ClockChip>}
            </>
          ) : paused ? (
            <span className="rounded-md bg-[#27272A] px-2 py-0.5 text-[11px] font-semibold uppercase text-[#a1a1aa]" data-testid={`paused-${event.id}`}>Paused</span>
          ) : event.status === "settled" ? (
            <span className="flex items-center gap-1 rounded-md bg-[#00D632]/12 px-2 py-0.5 text-[11px] font-semibold text-[#00D632]"><Icon name="check-circle" size={12} /> Final</span>
          ) : (
            <ClockChip><Icon name="clock" size={11} /> {event.start_time ? new Date(event.start_time).toLocaleString(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit" }) : "Upcoming"}</ClockChip>
          )}
        </div>
        {event.max_bet ? (
          <span className="rounded-md bg-[#00D632]/12 px-2 py-0.5 text-[11px] font-semibold text-[#00D632]">Max ${event.max_bet.toFixed(0)}</span>
        ) : null}
      </div>

      {/* teams */}
      {isOutright ? (
        <p className="mb-4 font-display text-lg font-black tracking-tight">{event.title}</p>
      ) : (
        <div className="mb-4 space-y-2" data-testid={liveScore ? `live-score-${event.id}` : undefined}>
          <TeamRow badge={event.away_team} name={event.away_team} score={awayScore} live={isLive} league={event.league} sport={event.sport} />
          <TeamRow badge={event.home_team} name={event.home_team} score={homeScore} live={isLive} league={event.league} sport={event.sport} />
        </div>
      )}

      {/* markets */}
      {isOutright ? (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {boardOutcomes.map((o) => (
              <SBOddsButton key={o.id} testid={`odds-${event.id}-${o.id}`} label={o.label} price={o.odds} size="md"
                selected={isPicked(event.id, o.id)} locked={locked || o.suspended === true || o.active === false} winner={event.status === "settled" && event.winning_outcome_id === o.id}
                onClick={() => onPick(event, o)} />
            ))}
          </div>
          {trueOutright && !settled && (
            <Link to={`/event/${event.id}/outright`} data-testid={`view-outright-${event.id}`}
              className="mt-3 flex items-center justify-between rounded-xl border border-[#1F1F23] bg-[#101013] px-3.5 py-2.5 text-sm font-semibold text-[#a1a1aa] transition-colors hover:border-[#00D632]/40 hover:text-white">
              <span className="flex items-center gap-1.5"><Icon name="layers" size={15} className="text-[#00D632]" /> {largeOutright ? `View all ${activeOutrightOutcomes.length} selections` : "Open outright market"}</span>
              <Icon name="chevron-right" size={14} />
            </Link>
          )}
        </>
      ) : (
        <div className="space-y-2.5">
          {groups.map((grp) => {
            const title = grp.market ? (grp.market === "spread" ? spreadTitle(sk(event.sport)) : MARKET_TITLE[grp.market]) : "Moneyline";
            const big = grp.market === "moneyline" || !grp.market;
            return (
              <div key={grp.market || "ml"} data-testid={`market-${event.id}-${grp.market || "moneyline"}`}>
                <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-[#71717a]">{title}</p>
                <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(grp.items.length, 3)}, minmax(0, 1fr))` }}>
                  {grp.items.map((o) => (
                    <SBOddsButton key={o.id} testid={`odds-${event.id}-${o.id}`}
                      label={shortLabel ? shortLabel(o.label, event) : o.label} price={o.odds} size={big ? "md" : "sm"}
                      selected={isPicked(event.id, o.id)} locked={locked || o.suspended === true || o.active === false}
                      winner={event.status === "settled" && event.winning_outcome_id === o.id}
                      onClick={() => onPick(event, o)} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* inline player-prop expander (child prop events) */}
      {activeProps.length > 0 && (
        <div className="mt-3 border-t border-[#1F1F23] pt-3">
          <button
            data-testid={`toggle-props-${event.id}`}
            onClick={() => setShowProps((v) => !v)}
            className="flex w-full items-center justify-between text-sm font-semibold text-white"
          >
            <span className="flex items-center gap-1.5"><Icon name="layers" size={15} className="text-[#00D632]" /> Player props &amp; over/unders ({activeProps.length})</span>
            <Icon name="chevron-down" size={16} className={`transition-transform ${showProps ? "rotate-180" : ""}`} />
          </button>
          <AnimatePresence>
            {showProps && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                <div className="mt-3 space-y-3">
                  {activeProps.map((p) => {
                    const pLocked = p.status !== "open";
                    return (
                      <div key={p.id} data-testid={`prop-${p.id}`}>
                        <p className="mb-1.5 text-xs font-semibold text-[#a1a1aa]">{p.title}{p.status === "paused" ? " · paused" : ""}</p>
                        <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(p.outcomes.length, 3)}, minmax(0, 1fr))` }}>
                          {p.outcomes.map((o) => (
                            <SBOddsButton key={o.id} testid={`odds-${p.id}-${o.id}`} label={o.label} price={o.odds} size="sm"
                              selected={isPicked(p.id, o.id)} locked={pLocked || o.suspended === true || o.active === false} onClick={() => onPick(p, o)} />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* all-markets footer link */}
      {!isOutright && !settled && (
        <Link to={`/event/${event.id}`} data-testid={`view-markets-${event.id}`}
          className="mt-3 flex items-center justify-between rounded-xl border border-[#1F1F23] bg-[#101013] px-3.5 py-2.5 text-sm font-semibold text-[#a1a1aa] transition-colors hover:border-[#00D632]/40 hover:text-white">
          <span className="flex items-center gap-1.5"><Icon name="layers" size={15} className="text-[#00D632]" /> All markets &amp; player props</span>
          <span className="flex items-center gap-1 tabular-nums text-[#71717a]">{marketCount} <Icon name="chevron-right" size={14} /></span>
        </Link>
      )}
    </motion.div>
  );
}

function TeamRow({ badge, name, score, live, league, sport }) {
  return (
    <div className="flex items-center gap-3">
      <TeamLogo name={badge} league={league} sport={sport} size={30} />
      <span className="min-w-0 flex-1 truncate font-display text-[15px] font-bold tracking-tight">{name}</span>
      {score != null && (
        <motion.span
          key={String(score)}
          initial={live ? { backgroundColor: "rgba(0,214,50,0.35)" } : false}
          animate={{ backgroundColor: "rgba(0,214,50,0)" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="rounded-md px-1.5 font-display text-xl font-black tabular-nums text-white"
        >
          {score}
        </motion.span>
      )}
    </div>
  );
}
